import type { InvestmentPortfolio } from '@fd/schemas';
import { ArrowRight } from 'lucide-react';
import { useEntities, type DashboardData } from '../app/data.ts';
import { useT } from '../app/i18n.tsx';
import { hrefFor } from '../app/router.tsx';
import { Card, Money } from '../ui/primitives.tsx';

// Cash savings and investments, each with its share of the total and its accounts.

export function SavingsCard({ data, className }: { data: DashboardData; className?: string }) {
  const t = useT();
  const currency = data.household.baseCurrency;
  const portfolios = useEntities<InvestmentPortfolio>('investmentPortfolio').data ?? [];
  const base = new Map(data.netWorth.accounts.map((v) => [v.accountId, v.baseMinor]));
  const accounts = data.accounts.filter((a) => a.isSavingsAsset && !a.archivedAt);
  const cash = accounts.filter((a) => a.type !== 'INVESTMENT');
  const invested = accounts.filter((a) => a.type === 'INVESTMENT');
  const sum = (list: typeof accounts) => list.reduce((s, a) => s + (base.get(a.id) ?? 0n), 0n);
  const cashTotal = sum(cash);
  const investedTotal = sum(invested);
  const total = cashTotal + investedTotal;
  const share = (part: bigint) => (total > 0n ? Number((part * 1000n) / total) / 10 : 0);
  const percent = new Intl.NumberFormat(t.locale, {
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  });

  const row = (account: (typeof accounts)[number]) => {
    const portfolio = portfolios.find((p) => p.accountId === account.id && p.deletedAt === null);
    const pnl = portfolio ? BigInt(portfolio.unrealizedPnlMinor) : null;
    const cost = portfolio ? BigInt(portfolio.investedMinor) - (pnl ?? 0n) : null;
    return (
      <div key={account.id} className="savings-row">
        <span className="grow truncate">{account.name}</span>
        <span className="stack" style={{ alignItems: 'flex-end', gap: 0 }}>
          <strong className="num">
            <Money minor={account.balanceMinor} currency={account.currency} animate />
          </strong>
          {pnl !== null && cost !== null && cost > 0n && (
            <span className={`savings-row__result ${pnl >= 0n ? 'fg-income' : 'fg-expense'}`}>
              <Money
                minor={pnl}
                currency={account.currency}
                options={{ signDisplay: 'exceptZero' }}
              />{' '}
              · {percent.format(Number((pnl * 10_000n) / cost) / 100)}%
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    <Card
      className={className}
      title={t.t('dashboard.savingsCard')}
      action={
        <a className="card__link" href={hrefFor({ page: 'savings' })}>
          {t.t('dashboard.viewAll')} <ArrowRight aria-hidden="true" />
        </a>
      }
    >
      <div className="stack gap-4">
        <div className="stack" style={{ gap: 4 }}>
          <span className="savings-total">
            <Money minor={total} currency={currency} animate />
          </span>
          <span className="list-row__meta amount-text">
            {t.t('savings.putAsideThisMonth')}:{' '}
            <Money minor={data.current.booked.savingsContributionMinor} currency={currency} />
          </span>
        </div>
        {cash.length > 0 && (
          <div className="stack" style={{ gap: 6 }}>
            <span className="savings-group">
              <span
                className="hero__dot"
                style={{ background: 'var(--series-3)' }}
                aria-hidden="true"
              />
              {t.t('netWorth.groupSavings')}
              <span className="grow" />
              <span className="num">
                {new Intl.NumberFormat(t.locale, { maximumFractionDigits: 0 }).format(
                  share(cashTotal),
                )}
                %
              </span>
            </span>
            {cash.map(row)}
          </div>
        )}
        {invested.length > 0 && (
          <div className="stack" style={{ gap: 6 }}>
            <span className="savings-group">
              <span
                className="hero__dot"
                style={{ background: 'var(--series-7)' }}
                aria-hidden="true"
              />
              {t.t('netWorth.groupInvestments')}
              <span className="grow" />
              <span className="num">
                {new Intl.NumberFormat(t.locale, { maximumFractionDigits: 0 }).format(
                  share(investedTotal),
                )}
                %
              </span>
            </span>
            {invested.map(row)}
          </div>
        )}
      </div>
    </Card>
  );
}
